/**
 * PairSuggestions Component (Phase 9.3)
 *
 * Shows top 3 heuristic-based "pairs well with" suggestions after adding legs.
 * Uses hardcoded NBA prop correlations — no API calls.
 */

import type { BuilderLeg } from '@parlay-picker/shared/builderLeg';
import { ChevronDown } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';

// =============================================================================
// Correlation Heuristics
// =============================================================================

/** Known positive correlations between NBA prop types (directional) */
const PROP_PAIR_HINTS: Array<{
  from: string;
  fromDir: 'Over' | 'Under';
  to: string;
  toDir: 'Over' | 'Under';
  phi: number;
  reason: string;
}> = [
  // Points ↔ Assists: high-scoring players facilitate
  {
    from: 'pts',
    fromDir: 'Over',
    to: 'ast',
    toDir: 'Over',
    phi: 0.35,
    reason: 'scoring + playmaking correlate',
  },
  {
    from: 'ast',
    fromDir: 'Over',
    to: 'pts',
    toDir: 'Over',
    phi: 0.35,
    reason: 'playmaking + scoring correlate',
  },
  // Points ↔ Threes: shooters score via 3s
  {
    from: 'pts',
    fromDir: 'Over',
    to: '3pm',
    toDir: 'Over',
    phi: 0.42,
    reason: '3-pt shooting drives scoring',
  },
  {
    from: '3pm',
    fromDir: 'Over',
    to: 'pts',
    toDir: 'Over',
    phi: 0.42,
    reason: 'threes boost total points',
  },
  // Rebounds ↔ Blocks: rim presence
  {
    from: 'reb',
    fromDir: 'Over',
    to: 'blk',
    toDir: 'Over',
    phi: 0.31,
    reason: 'rim protectors grab boards',
  },
  {
    from: 'blk',
    fromDir: 'Over',
    to: 'reb',
    toDir: 'Over',
    phi: 0.31,
    reason: 'shot blockers are rebounders',
  },
  // Assists ↔ Steals: active hands
  {
    from: 'ast',
    fromDir: 'Over',
    to: 'stl',
    toDir: 'Over',
    phi: 0.22,
    reason: 'active hands on both ends',
  },
  // Under correlations: low-scoring game → multiple unders
  {
    from: 'pts',
    fromDir: 'Under',
    to: 'ast',
    toDir: 'Under',
    phi: 0.3,
    reason: 'fewer points = fewer assists',
  },
  {
    from: 'pts',
    fromDir: 'Under',
    to: '3pm',
    toDir: 'Under',
    phi: 0.38,
    reason: 'cold shooting = fewer 3s',
  },
];

interface PairSuggestion {
  player: string;
  propType: string;
  direction: 'Over' | 'Under';
  phi: number;
  reason: string;
  sourceLeg: string;
}

// =============================================================================
// Component
// =============================================================================

interface PairSuggestionsProps {
  legs: BuilderLeg[];
}

export function PairSuggestions({ legs }: PairSuggestionsProps): ReactElement | null {
  const [isOpen, setIsOpen] = useState(false);

  const suggestions = useMemo((): PairSuggestion[] => {
    if (legs.length === 0 || legs.length > 3) return [];

    const results: PairSuggestion[] = [];
    const existingKeys = new Set(
      legs.map((l) => `${(l.player ?? '').toLowerCase()}|${l.statType}`),
    );

    for (const leg of legs) {
      if (!leg.player || !leg.statType || !leg.overUnder) continue;

      // Find same-game teammates (other legs from the same game, different player)
      const teammates = legs.filter(
        (other) =>
          other.id !== leg.id &&
          other.gameId &&
          other.gameId === leg.gameId &&
          other.player &&
          other.player !== leg.player,
      );

      // Suggest complementary props for teammates in the same game
      for (const mate of teammates) {
        if (!mate.statType || !mate.overUnder) continue;
        for (const hint of PROP_PAIR_HINTS) {
          if (hint.from !== leg.statType || hint.fromDir !== leg.overUnder) continue;
          if (hint.to === mate.statType) continue; // Already have this prop for this player
          const key = `${(mate.player ?? '').toLowerCase()}|${hint.to}`;
          if (existingKeys.has(key)) continue;
          results.push({
            player: mate.player ?? '',
            propType: hint.to,
            direction: hint.toDir,
            phi: hint.phi,
            reason: hint.reason,
            sourceLeg: leg.player,
          });
        }
      }

      // Suggest complementary props for the SAME player (different stat type)
      for (const hint of PROP_PAIR_HINTS) {
        if (hint.from !== leg.statType || hint.fromDir !== leg.overUnder) continue;
        const key = `${(leg.player).toLowerCase()}|${hint.to}`;
        if (existingKeys.has(key)) continue;
        results.push({
          player: leg.player,
          propType: hint.to,
          direction: hint.toDir,
          phi: hint.phi,
          reason: hint.reason,
          sourceLeg: leg.player,
        });
      }
    }

    // Deduplicate by player+prop, keeping highest phi
    const seen = new Map<string, PairSuggestion>();
    for (const s of results) {
      const dedupeKey = `${s.player.toLowerCase()}|${s.propType}|${s.direction}`;
      const existing = seen.get(dedupeKey);
      if (!existing || s.phi > existing.phi) {
        seen.set(dedupeKey, s);
      }
    }

    return [...seen.values()].sort((a, b) => b.phi - a.phi).slice(0, 3);
  }, [legs]);

  if (suggestions.length === 0) return null;

  const propLabel = (pt: string): string => {
    const labels: Record<string, string> = {
      pts: 'points',
      reb: 'rebounds',
      ast: 'assists',
      blk: 'blocks',
      stl: 'steals',
      '3pm': 'threes',
    };
    return labels[pt] ?? pt;
  };

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-1.5 text-[11px] text-purple-400 hover:text-purple-300 transition-colors"
      >
        <span>Pairs well with</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="mt-1 space-y-0.5 pl-2 border-l border-purple-500/20">
          {suggestions.map((s) => (
            <div
              key={`${s.player}-${s.propType}-${s.direction}`}
              className="text-[11px] text-theme-text-secondary"
            >
              <span className="text-theme-text">{s.player}</span>{' '}
              <span>
                {propLabel(s.propType)} {s.direction}
              </span>{' '}
              <span className="text-purple-400/70">
                (+{s.phi.toFixed(2)} corr — {s.reason})
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
